/**
 * Sidra motion.
 *
 * Four jobs: flag the body once the page has scrolled so the header can grow a
 * shadow, reveal sections as they come into view with their cards one by one,
 * write section headings in word by word, and count figures up from zero.
 *
 * THE RULE THAT SHAPES THIS FILE
 * The reveal hides content until something shows it again, so "something"
 * must not be a single mechanism that can quietly fail. Horizon scrolls an
 * inner .page-wrapper rather than the document, sections load late, and the
 * theme editor swaps them out mid-flight. Any one trigger will miss a case,
 * and a missed case is a blank page.
 *
 * So there are three, in order of how quickly they fire:
 *   1. IntersectionObserver, which does not care which element scrolls
 *   2. a rect check on scroll and resize, throttled to one animation frame
 *   3. a per-section deadline that shows it regardless
 *
 * The deadline is the one that matters. Whatever the first two miss, it
 * catches, because no animation is worth a visitor staring at nothing.
 *
 * The reveal writes a transform on the section for the length of the animation
 * and removes it afterwards. That matters: a transform on an ancestor breaks
 * position: fixed inside it, so the class never outlives the transition, and
 * the header group and the cart drawer are skipped entirely.
 */
(() => {
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Below this the viewport is not something a person is looking at: a hidden
  // tab, a thumbnail renderer, a headless pass. Hiding sections there risks
  // hiding them for good.
  const MIN_VIEWPORT = 300;

  // Nothing stays hidden longer than this after its section was prepared,
  // whatever the scroll position or the state of the observer.
  const DEADLINE = 2600;

  const SKIP = [
    '.header-section',
    '.shopify-section-group-header-group',
    '#shopify-section-cart-drawer-section',
  ];

  // Children that arrive one after the other inside a revealing section.
  const STAGGER = [
    '.sidra-grid__item',
    '.sidra-rv__card',
    '.sidra-usp__item',
    '.sidra-pl__item',
    '.sidra-bl__step',
    '.product-grid > li',
  ].join(', ');

  // Headings that write themselves in word by word.
  const WRITE = [
    '.sidra-head__title',
    '.sidra-split__title',
    '.sidra-rv__title',
    '.sidra-pl__title',
    '.sidra-bl__title',
    '.sidra-signup__title',
  ].join(', ');

  // Past this many words the line stops falling further behind, so a long
  // heading does not end on a two second wait.
  const WRITE_CAP = 12;

  // Figures that count up from zero. The bundle rungs read "20% off" and
  // "5th free": a leading number with text after it, which countUp handles.
  const COUNTERS = '.sidra-split__big, .sidra-bl__reward';

  /** The scroll offset, whichever element happens to be doing the scrolling. */
  const scrollOffset = () => {
    if (window.scrollY > 0) return window.scrollY;
    const wrapper = document.querySelector('.page-wrapper');
    return wrapper ? wrapper.scrollTop : 0;
  };

  /* ---- header shadow ---------------------------------------------------- */

  const flagScroll = () => {
    document.body.classList.toggle('sidra-scrolled', scrollOffset() > 24);
  };

  /* ---- reveal ----------------------------------------------------------- */

  const pending = new Set();
  const counters = new Set();

  const settle = (element) => {
    element.classList.remove('sidra-reveal', 'is-in');
    for (const child of element.querySelectorAll('.sidra-stagger')) {
      child.classList.remove('sidra-stagger');
      child.style.removeProperty('--sidra-i');
    }
  };

  // Fires regardless of which element does the scrolling, which the rect check
  // cannot promise on a theme that scrolls an inner wrapper.
  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) reveal(entry.target);
            }
          },
          { rootMargin: '0px 0px -8% 0px' }
        )
      : null;

  function reveal(element) {
    if (!pending.has(element)) return;
    pending.delete(element);
    if (observer) observer.unobserve(element);
    element.classList.add('is-in');

    const done = (event) => {
      if (event && event.target !== element) return;
      element.removeEventListener('transitionend', done);
      settle(element);
    };

    element.addEventListener('transitionend', done);
    // A section that never fires transitionend, because it was hidden or
    // replaced mid-flight, still has to be cleaned up. The wait covers the
    // longest word delay plus the transition itself.
    window.setTimeout(() => done(), 2400);
  }

  /** Shows a section outright, animation skipped. */
  const showNow = (element) => {
    pending.delete(element);
    if (observer) observer.unobserve(element);
    settle(element);
  };

  /* ---- headings written word by word ------------------------------------ */

  /**
   * Wraps every word of a heading in its own span so the CSS can wipe them in
   * from the left, one after the other. Walks the text nodes rather than
   * rewriting innerHTML, because these headings carry markup of their own: the
   * gold italic span around a highlighted word.
   *
   * Text with no whitespace in front of it is glued onto the previous word
   * rather than becoming a word of its own. Without that, the comma after a
   * highlighted word becomes a separate inline block, and the line is then
   * free to break in front of it.
   */
  const writeWords = (heading) => {
    if (heading.dataset.sidraWritten) return;
    heading.dataset.sidraWritten = 'yes';

    const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
    const textNodes = [];
    while (walker.nextNode()) textNodes.push(walker.currentNode);

    let index = 0;
    let lastWord = null;

    for (const node of textNodes) {
      const value = node.nodeValue;

      if (!value.trim()) {
        if (value) lastWord = null;
        continue;
      }

      let glue = lastWord !== null && !/^\s/.test(value);
      const fragment = document.createDocumentFragment();

      for (const part of value.split(/(\s+)/)) {
        if (!part) continue;

        if (!part.trim()) {
          fragment.appendChild(document.createTextNode(part));
          lastWord = null;
          glue = false;
          continue;
        }

        if (glue) {
          lastWord.appendChild(document.createTextNode(part));
          glue = false;
          continue;
        }

        const word = document.createElement('span');
        word.className = 'sidra-write__w';
        word.style.setProperty('--sidra-w', String(Math.min(index, WRITE_CAP)));
        word.textContent = part;
        fragment.appendChild(word);
        lastWord = word;
        index += 1;
      }

      node.parentNode.replaceChild(fragment, node);
    }
  };

  /* ---- counting up ------------------------------------------------------ */

  const countUp = (element) => {
    counters.delete(element);

    const original = element.textContent;
    const match = original.trim().match(/^(\d[\d.,]*)([\s\S]*)$/);
    if (!match) return;

    const target = parseInt(match[1].replace(/[.,]/g, ''), 10);
    if (!Number.isFinite(target) || target < 2 || target > 1000000) return;

    // This figure can carry a claim about donated meals, so it must never be
    // left sitting on a half counted number. A hidden tab pauses
    // requestAnimationFrame, so counting only starts on a visible page, and a
    // plain timer, which keeps running when frames do not, always writes the
    // real text back.
    if (document.visibilityState !== 'visible') return;

    const suffix = match[2];
    const duration = 900;
    const started = performance.now();
    const restore = () => {
      element.textContent = original;
    };
    const safety = window.setTimeout(restore, duration + 2000);

    const tick = (now) => {
      const progress = Math.min((now - started) / duration, 1);

      if (progress < 1) {
        const eased = 1 - Math.pow(1 - progress, 3);
        element.textContent = Math.round(target * eased) + suffix;
        window.requestAnimationFrame(tick);
        return;
      }

      window.clearTimeout(safety);
      restore();
    };

    element.textContent = '0' + suffix;
    window.requestAnimationFrame(tick);
  };

  /* ---- the check -------------------------------------------------------- */

  const check = () => {
    if (pending.size === 0 && counters.size === 0) return;

    const revealLimit = window.innerHeight * 0.92;
    for (const element of pending) {
      if (element.getBoundingClientRect().top < revealLimit) reveal(element);
    }

    const countLimit = window.innerHeight * 0.85;
    for (const element of counters) {
      if (element.getBoundingClientRect().top < countLimit) countUp(element);
    }
  };

  const prepare = () => {
    if (prefersReducedMotion.matches) return;
    if (window.innerHeight < MIN_VIEWPORT) return;

    const sections = document.querySelectorAll(
      '#MainContent > .shopify-section, footer > .shopify-section'
    );

    for (const section of sections) {
      if (section.classList.contains('sidra-reveal')) continue;
      if (SKIP.some((selector) => section.matches(selector))) continue;

      // Anything already on screen stays where it is. Only what sits below the
      // fold is worth revealing, otherwise the page flashes on load.
      if (section.getBoundingClientRect().top < window.innerHeight * 0.9) continue;

      section.classList.add('sidra-reveal');
      pending.add(section);
      if (observer) observer.observe(section);

      // The deadline. Whatever the observer and the scroll check do or do not
      // do, this section is visible by then: animated if it is on screen,
      // shown outright if it is not.
      window.setTimeout(() => {
        if (!pending.has(section)) return;
        if (section.getBoundingClientRect().top < window.innerHeight) {
          reveal(section);
        } else {
          showNow(section);
        }
      }, DEADLINE);

      const children = section.querySelectorAll(STAGGER);
      children.forEach((child, index) => {
        child.classList.add('sidra-stagger');
        // Capped, so a grid of thirty tiles does not end on a two second wait.
        child.style.setProperty('--sidra-i', String(Math.min(index, 8)));
      });

      for (const heading of section.querySelectorAll(WRITE)) writeWords(heading);
    }

    for (const figure of document.querySelectorAll(COUNTERS)) {
      if (figure.dataset.sidraCounted) continue;
      figure.dataset.sidraCounted = 'yes';
      counters.add(figure);
    }
  };

  /* ---- wiring ----------------------------------------------------------- */

  let frame = 0;
  const onScroll = () => {
    if (frame) return;
    frame = window.requestAnimationFrame(() => {
      frame = 0;
      flagScroll();
      check();
    });
  };

  document.addEventListener('scroll', onScroll, { passive: true, capture: true });
  window.addEventListener('resize', onScroll, { passive: true });

  const start = () => {
    prepare();
    flagScroll();
    check();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Images settling and fonts swapping both move sections around after parse.
  window.addEventListener('load', () => {
    prepare();
    check();
  });

  // The theme editor swaps sections out from under us.
  document.addEventListener('shopify:section:load', start);
})();
